import React from 'react';
import { connect } from 'react-redux';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { ExternalLink, LayoutSingleColumn, Page } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import marketingCss from '../_marketingPage/marketingPage.module.css';
import css from './HowToBuyPage.module.css';

const PurchaseStep = ({ index, headingId, bodyId }) => (
  <div className={css.step}>
    <h3 className={css.stepHeading}>
      <span className={css.stepNumber}>{index}</span>
      <FormattedMessage id={headingId} />
    </h3>
    <p className={css.body}>
      <FormattedMessage id={bodyId} />
    </p>
  </div>
);

const StepCard = ({ codeId, headingId, bodyId }) => (
  <div className={css.stepCard}>
    <p className={css.stepCode}>
      <FormattedMessage id={codeId} />
    </p>
    <h3 className={css.stepCardHeading}>
      <FormattedMessage id={headingId} />
    </h3>
    <p className={css.stepCardBody}>
      <FormattedMessage id={bodyId} />
    </p>
  </div>
);

const SubBlock = ({ headingId, bodyId }) => (
  <div className={css.subBlock}>
    <h3 className={css.subBlockHeading}>
      <FormattedMessage id={headingId} />
    </h3>
    <p className={css.body}>
      <FormattedMessage id={bodyId} />
    </p>
  </div>
);

/**
 * "Slik kjøper du" page (`/how-to-buy`).
 *
 * Buyer-facing magazine page. Same single-column / serif-heading
 * language as HowToSellPage, but text-only — per client direction this
 * page doesn't carry any inline photography. Content per the May 2026
 * implementation brief (Zuprize_Slik_Kjoper_Du_260526.pdf).
 *
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled
 * @returns {JSX.Element}
 */
const HowToBuyPageComponent = props => {
  const intl = useIntl();
  const { scrollingDisabled } = props;

  const supportEmail = 'post@zuprize.no';
  const emailLink = (
    <ExternalLink href={`mailto:${supportEmail}`} className={marketingCss.kontaktLink}>
      {supportEmail}
    </ExternalLink>
  );

  return (
    <Page
      title={intl.formatMessage({ id: 'HowToBuyPage.metaTitle' })}
      description={intl.formatMessage({ id: 'HowToBuyPage.metaDescription' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer currentPage="HowToBuyPage" />} footer={<FooterContainer />}>
        <article className={css.root}>
          <div className={css.content}>
            <header className={css.hero}>
              <h1 className={css.heroHeading}>
                <FormattedMessage id="HowToBuyPage.heroHeading" />
              </h1>
              <p className={css.heroBody}>
                <FormattedMessage id="HowToBuyPage.heroBody" />
              </p>
            </header>

            <section className={css.stepsRow}>
              <StepCard
                codeId="HowToBuyPage.badge1Code"
                headingId="HowToBuyPage.badge1Heading"
                bodyId="HowToBuyPage.badge1Body"
              />
              <StepCard
                codeId="HowToBuyPage.badge2Code"
                headingId="HowToBuyPage.badge2Heading"
                bodyId="HowToBuyPage.badge2Body"
              />
              <StepCard
                codeId="HowToBuyPage.badge3Code"
                headingId="HowToBuyPage.badge3Heading"
                bodyId="HowToBuyPage.badge3Body"
              />
            </section>

            <section className={css.section}>
              <p className={css.sectionEyebrow}>
                <FormattedMessage id="HowToBuyPage.curatedEyebrow" />
              </p>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="HowToBuyPage.curatedHeading" />
              </h2>
              <p className={css.body}>
                <FormattedMessage id="HowToBuyPage.curatedBody" />
              </p>
            </section>

            <section className={css.section}>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="HowToBuyPage.stepsHeading" />
              </h2>
              <p className={css.sectionLede}>
                <FormattedMessage id="HowToBuyPage.stepsLede" />
              </p>

              <PurchaseStep
                index="01"
                headingId="HowToBuyPage.step1Heading"
                bodyId="HowToBuyPage.step1Body"
              />
              <PurchaseStep
                index="02"
                headingId="HowToBuyPage.step2Heading"
                bodyId="HowToBuyPage.step2Body"
              />
              <PurchaseStep
                index="03"
                headingId="HowToBuyPage.step3Heading"
                bodyId="HowToBuyPage.step3Body"
              />
              <PurchaseStep
                index="04"
                headingId="HowToBuyPage.step4Heading"
                bodyId="HowToBuyPage.step4Body"
              />
              <PurchaseStep
                index="05"
                headingId="HowToBuyPage.step5Heading"
                bodyId="HowToBuyPage.step5Body"
              />
              <PurchaseStep
                index="06"
                headingId="HowToBuyPage.step6Heading"
                bodyId="HowToBuyPage.step6Body"
              />
              <PurchaseStep
                index="07"
                headingId="HowToBuyPage.step7Heading"
                bodyId="HowToBuyPage.step7Body"
              />
            </section>

            <blockquote className={css.pullQuote}>
              <FormattedMessage id="HowToBuyPage.pullQuote" />
            </blockquote>

            <section className={css.section}>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="HowToBuyPage.protectionHeading" />
              </h2>
              <p className={css.body}>
                <FormattedMessage id="HowToBuyPage.protectionLede" />
              </p>
              <SubBlock
                headingId="HowToBuyPage.protection1Heading"
                bodyId="HowToBuyPage.protection1Body"
              />
              <SubBlock
                headingId="HowToBuyPage.protection2Heading"
                bodyId="HowToBuyPage.protection2Body"
              />
              <SubBlock
                headingId="HowToBuyPage.protection3Heading"
                bodyId="HowToBuyPage.protection3Body"
              />
              <SubBlock
                headingId="HowToBuyPage.protection4Heading"
                bodyId="HowToBuyPage.protection4Body"
              />
              <SubBlock
                headingId="HowToBuyPage.protection5Heading"
                bodyId="HowToBuyPage.protection5Body"
              />
            </section>

            <section className={css.section}>
              <p className={css.sectionEyebrow}>
                <FormattedMessage id="HowToBuyPage.responsibilityEyebrow" />
              </p>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="HowToBuyPage.responsibilityHeading" />
              </h2>
              <p className={css.body}>
                <FormattedMessage id="HowToBuyPage.responsibilityBody" />
              </p>
            </section>

            <section className={css.section}>
              <p className={css.sectionEyebrow}>
                <FormattedMessage id="HowToBuyPage.priceEyebrow" />
              </p>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="HowToBuyPage.priceHeading" />
              </h2>
              <p className={css.body}>
                <FormattedMessage id="HowToBuyPage.priceBody" />
              </p>
            </section>

            <section className={css.section}>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="HowToBuyPage.ctaHeading" />
              </h2>
              <p className={css.body}>
                <FormattedMessage id="HowToBuyPage.ctaBody" />
              </p>
            </section>

            <p className={css.signOff}>
              <FormattedMessage id="HowToBuyPage.signOff" />
            </p>
          </div>

          <section className={marketingCss.kontakt}>
            <div className={marketingCss.kontaktInner}>
              <p className={marketingCss.kontaktEyebrow}>
                <FormattedMessage id="HowToBuyPage.kontaktEyebrow" />
              </p>
              <p className={marketingCss.kontaktBody}>
                <FormattedMessage
                  id="HowToBuyPage.kontaktBody"
                  values={{ br: <br />, email: emailLink }}
                />
              </p>
            </div>
          </section>
        </article>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const HowToBuyPage = connect(mapStateToProps)(HowToBuyPageComponent);

export { HowToBuyPageComponent };
export default HowToBuyPage;
